import React from 'react';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    title: 'Step 1: Choose Package and Signup',
    detail: 'Open Signup, select the package that fits your company, fill company details, and create the company owner login.',
    actions: ['Click User Manual or Signup from login screen.', 'Choose package as per admin and worker limit.', 'Submit company name, contact details, username, and password.'],
  },
  {
    title: 'Step 2: Login to Your Workspace',
    detail: 'Use the username and password created during signup. After login, the software opens your role-based dashboard.',
    actions: ['Company owner sees company-level menus.', 'Admin sees assigned site and worker menus.', 'Worker sees transaction and request menus.'],
  },
  {
    title: 'Step 3: Create Sites',
    detail: 'Create every building, branch, project, or work location where transactions will happen.',
    actions: ['Open Attributes > Add Site.', 'Enter site name and save.', 'Use the same site while creating admins, workers, and transactions.'],
  },
  {
    title: 'Step 4: Create Attributes',
    detail: 'Attributes are transaction categories. A main head can have many sub heads.',
    actions: ['Open Attributes > Add Attribute.', 'Example: create main head Travel once.', 'Add sub heads like Petrol, Fuel, Taxi, Parking under Travel.'],
  },
  {
    title: 'Step 5: Create Admins and Workers',
    detail: 'Create users one by one or upload many users together with CSV or Excel.',
    actions: ['Open User Management > Create User for single user.', 'Open bulk upload to import many users.', 'Assign admins and workers to the correct site or building.'],
  },
  {
    title: 'Step 6: Give Funds',
    detail: 'Company owner can give funds to admins and workers. Admins can give funds to their assigned workers.',
    actions: ['Use transaction form or fund flow screens as per your role.', 'Select receiver, site, amount, and remarks.', 'Check reports after fund transfer.'],
  },
  {
    title: 'Step 7: Request Funds',
    detail: 'Admins can request funds from the company owner. Workers can request funds from their admin.',
    actions: ['Open request section.', 'Enter amount, purpose, and related details.', 'Track approval status in request reports.'],
  },
  {
    title: 'Step 8: Add Daily Transactions',
    detail: 'Workers and allowed users enter expenses or fund usage from the transaction form.',
    actions: ['Open Daily Txn Book > Transaction Form.', 'Select site, main head, sub head, amount, date, and remarks.', 'Upload proof photo when photo proof is enabled.'],
  },
  {
    title: 'Step 9: Review Reports',
    detail: 'Reports help you track users, requests, transactions, returns, balances, and ledger movement.',
    actions: ['Use Detailed Record for complete transaction details.', 'Use Ledger Reports for fund movement and balance view.', 'Use User Report and See User Details for user-level records.'],
  },
  {
    title: 'Step 10: Manage Account Changes',
    detail: 'Company owner can request package upgrade, plan renewal, email change, or ownership transfer.',
    actions: ['Open Company Management > Account Requests.', 'Choose request type and enter details.', 'The TxnAlgo2Bot team reviews and connects with you.'],
  },
];

const UserManual = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-indigo-600">TxnAlgo2Bot Guide</div>
            <h1 className="mt-2 text-3xl font-black tracking-tight">User Manual</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Start from step 1 and follow the setup flow. This guide is written for company owners, admins, and workers.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/signup')} className="rounded-lg border border-indigo-200 px-4 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-50">Signup</button>
            <button onClick={() => navigate('/')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">Login</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="space-y-4">
          {steps.map((step) => (
            <section key={step.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-black text-slate-900">{step.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{step.detail}</p>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {step.actions.map((action) => (
                  <div key={action} className="rounded-lg bg-slate-50 p-3 text-sm font-medium leading-6 text-slate-700">
                    {action}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-6 rounded-lg border border-indigo-100 bg-indigo-50 p-5">
          <h2 className="text-lg font-black text-indigo-950">Quick Report Guide</h2>
          <div className="mt-4 grid gap-3 text-sm text-indigo-950 md:grid-cols-2">
            <p><b>User Report:</b> user list, role, and assigned details.</p>
            <p><b>Request Reports:</b> fund and return request status.</p>
            <p><b>Detailed Record:</b> full transaction history with filters.</p>
            <p><b>Ledger Reports:</b> balance movement and account summary.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default UserManual;
